import {
  IContractAnchorBillingDay,
  IOfferingRes,
  IQuoteOfferingDeltaView,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
  IQuoteTemplateConfigSchema,
  IRateResBaseSchema,
  IRateResSchema,
  IUser,
  QuoteOfferingViolationLabelEnum,
  QuoteStatusEnum,
  QuoteTypeEnum,
  RateTypeEnum,
  SubscriptionTimingEnum,
} from '@monetize/types/app';
import { ALL_CURRENCIES, Maybe } from '@monetize/types/common';
import { QueryClient } from '@tanstack/react-query';
import { addDays } from 'date-fns/addDays';
import { differenceInDays } from 'date-fns/differenceInDays';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import { ReactElement, useState } from 'react';
import { handleApiErrorToast } from '../../../api/axios';
import {
  CPQ_SERVICE_API,
  doCreateQuoteOffering,
  doGetQuote,
} from '../../../api/cpqService';
import { doGetOfferingRate } from '../../../api/productCatalogService';
import { enqueueForQuote } from '../../../api/quoteWriteChain';
import { doGetTenantUser } from '../../../api/usersService';
import { MText } from '../../../components/Monetize';
import {
  CUSTOM_SELECT_SUBTITLE_TYPE,
  OPTION_TYPE_FIELD,
} from '../../../constants/customSelect';
import { OFFERING_TYPES_LABEL_DISPLAY } from '../../../constants/offerings';
import {
  DEFAULT_QUOTE_COLORS,
  QUOTE_ESIGN_CONTACT_EDITABLE_STATUSES,
} from '../../../constants/quotes';
import { useAuth } from '../../../services/auth0';
import { logger } from '../../../services/logger';
import { groupBy } from '../../../utils/misc';

export interface CreatedByUserDataTypes {
  loading: boolean;
  createdByUser: IUser | null;
  fetchCreatedByUser: (userId: string) => Promise<IUser | null>;
}

/**
 * This is no longer used anywhere in the application.
 * Prefer to use quote.ownerName instead.
 */
export const useCreatedByUser = (): CreatedByUserDataTypes => {
  const [loading, setLoading] = useState<boolean>(false);
  const [createdByUser, setCreatedByUser] = useState<IUser | null>(null);
  const { tenantId } = useAuth();

  const fetchCreatedByUser = async (userId: string): Promise<IUser | null> => {
    setLoading(true);
    try {
      const user = await doGetTenantUser(tenantId, userId);

      setCreatedByUser(user);
      return user;
    } catch (error) {
      handleApiErrorToast(error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    createdByUser,
    fetchCreatedByUser,
  };
};

const getTooltipLabel = (quoteType: string): string => {
  switch (quoteType) {
    case QuoteTypeEnum.AMENDMENT:
      return 'Amendments allow you to update quotes after a quote has been processed.';
    case QuoteTypeEnum.RENEWAL:
      return 'Renewals allow you to take an existing quote and create an entirely new quote.';
    default:
      return '';
  }
};

export const getQuoteTagColor = (quoteType: QuoteTypeEnum) => {
  switch (quoteType) {
    case 'AMENDMENT':
    case 'RENEWAL':
      return {
        color: 'tPurple.safety',
        bgColor: 'tPurple.inputBox',
      };
    default:
      return DEFAULT_QUOTE_COLORS;
  }
};

export const getQuoteDescription = (accountName?: string) => {
  if (accountName) {
    return `Quote for ${accountName}`;
  }

  return 'Quote ';
};

/**
 * Parse the `fieldMapping` query param that CRMs set when redirecting into
 * quote creation.
 *
 * The mapped currency may come from a free-text CRM field, so an unrecognized
 * code is dropped here rather than being applied to the form or sent in the
 * create payload.
 */
export const parseCrmQuoteFieldMapping = (
  quoteFieldMapping: Maybe<string>,
): Record<string, any> => {
  if (!quoteFieldMapping) {
    return {};
  }

  try {
    const parsedFieldMapping = JSON.parse(quoteFieldMapping);
    const { currency } = parsedFieldMapping;

    // Drop empty/null values too: they would otherwise overwrite the account
    // default currency when the mapping is spread over the create request.
    if (
      'currency' in parsedFieldMapping &&
      !(typeof currency === 'string' && Object.hasOwn(ALL_CURRENCIES, currency))
    ) {
      logger.warn(`Ignoring unrecognized CRM mapped currency: ${currency}`);
      delete parsedFieldMapping.currency;
    }

    return parsedFieldMapping;
  } catch (error) {
    logger.error('unable to parse quote field mapping\t', error);
    return {};
  }
};

/**
 * Since quote has many places that do not use ReactQuery, this is a helper method
 * for updating the quote in the query cache.
 */
export const updateQuoteQueryCache = (
  queryClient: QueryClient,
  updatedQuote: IQuoteRespSchema,
) => {
  const queryKey = CPQ_SERVICE_API.cpqServiceQuotes.byId?.queryKey?.(
    updatedQuote.id,
  );
  if (queryKey) {
    queryClient.setQueryData(queryKey, updatedQuote);
  }
};

/**
 * Returns offeringList based on inactive offering
 */
export const getOfferingList = (
  allOfferings: IOfferingRes[],
  inactiveOffering?: IOfferingRes,
) => {
  if (inactiveOffering) {
    const isInactiveOfferingActuallyActive = allOfferings.find(
      ({ id }) => id === inactiveOffering.id,
    );
    if (!isInactiveOfferingActuallyActive) {
      return [
        ...allOfferings,
        {
          name: 'Inactive',
          [OPTION_TYPE_FIELD]: CUSTOM_SELECT_SUBTITLE_TYPE,
        },
        {
          ...inactiveOffering,
          rightLabel: OFFERING_TYPES_LABEL_DISPLAY[inactiveOffering.type],
        },
      ];
    }

    return allOfferings;
  }

  return allOfferings;
};

/**
 * Get all Quote Offerings from a quote and create a payload that can be used to create the same offerings for a new quote
 *
 * This will ensure that any account specific rates that are from other accounts are replaced with the CATALOG rate
 * Units (quantities), discounts (both offering-level and custom item-level), and schedule changes are preserved from the original QuoteOfferings
 * Offering dates are adjusted relative to the target quote's contract period
 *
 * @param quoteIdToImport - The ID of the quote to import offerings from
 * @param accountId - The account ID of the target quote
 * @param targetContractStartDate - The contract start date of the target quote (to adjust offering dates)
 * @returns both parent offerings and schedule changes separately to support two-phase import
 */
export const getQuoteOfferingsToImport = async (
  quoteIdToImport: string,
  accountId: string,
  targetContractStartDate: string,
): Promise<{
  parentOfferings: IQuoteOfferingReqSchema[];
  scheduleChanges: Array<{
    oldParentId: string;
    parentOfferingId: string; // The offeringId of the parent (stays consistent across quotes)
    startDate: string;
    endDate: string;
    offering: IQuoteOfferingReqSchema;
  }>;
}> => {
  const quoteToImport = await doGetQuote(quoteIdToImport);
  if (!quoteToImport) {
    throw new Error('Quote not found');
  }

  // Helper function to ensure that a failure to fetch a rate does not result in an exception
  async function fetchRate(rateId: string) {
    try {
      return await doGetOfferingRate(rateId);
    } catch (ex) {
      logger.warn('Failed to get rate', ex);
    }
  }

  // Check all rates and make sure that any account specific rates from different accounts are replaced with the CATALOG rate
  // Group by productId for easy lookup
  let ratesById = groupBy(
    (
      await Promise.all(
        Array.from(
          new Set(quoteToImport.quoteOfferings.map(({ rateId }) => rateId)),
        ).map((rateId) => fetchRate(rateId)),
      )
    ).filter(Boolean) as IRateResSchema[],
    'id',
  );

  // Fallback to CATALOG rate if the provided quote is using an account specific rate from a different account
  const { quoteOfferings, parentRates } = quoteToImport.quoteOfferings.reduce(
    (
      output: {
        quoteOfferings: IQuoteOfferingRespSchema[];
        parentRates: Set<string>;
      },
      quoteOffering,
    ) => {
      const rate = ratesById[quoteOffering.rateId];
      if (
        rate &&
        rate.rateType === RateTypeEnum.ACCOUNT &&
        rate.parentRateId &&
        rate.accountId !== accountId
      ) {
        output.quoteOfferings.push({
          ...quoteOffering,
          rateId: rate.parentRateId,
        });
        output.parentRates.add(rate.parentRateId);
      } else {
        output.quoteOfferings.push(quoteOffering);
      }
      return output;
    },
    { quoteOfferings: [], parentRates: new Set<string>() },
  );

  // If CATALOG rates were replaced with actual rates, fetch these rates so proper min/max can be set
  ratesById = {
    ...ratesById,
    ...groupBy(
      (
        await Promise.all(
          Array.from(parentRates).map((rateId) => fetchRate(rateId)),
        )
      ).filter(Boolean) as IRateResSchema[],
      'id',
    ),
  };

  // Get payload for new quoteOfferings (separates parents and schedule changes)
  // Pass the source and target contract dates for date adjustment
  return getQuoteOfferingCreatePayloadFromQuoteOffering(
    quoteOfferings,
    ratesById,
    quoteToImport.contractStartDate,
    targetContractStartDate,
  );
};

/**
 * Convert existing QuoteOfferings into a request payload that can be used to create the same offerings for a new quote
 *
 * This function handles:
 * - Preserving units (quantities), discounts, and custom prices
 * - Adjusting offering dates relative to the new quote's contract period
 * - Separating parent offerings and schedule changes for two-phase import
 *
 * @param existingOfferings - The offerings from the source quote
 * @param ratesById - Map of rate IDs to rate objects
 * @param sourceContractStartDate - The contract start date of the source quote
 * @param targetContractStartDate - The contract start date of the target quote
 */
export const getQuoteOfferingCreatePayloadFromQuoteOffering = (
  existingOfferings: IQuoteOfferingRespSchema[],
  ratesById: Record<string, IRateResSchema>,
  sourceContractStartDate: string,
  targetContractStartDate: string,
): {
  parentOfferings: IQuoteOfferingReqSchema[];
  scheduleChanges: Array<{
    oldParentId: string;
    parentOfferingId: string;
    startDate: string;
    endDate: string;
    offering: IQuoteOfferingReqSchema;
  }>;
} => {
  if (!existingOfferings || !existingOfferings.length) {
    return { parentOfferings: [], scheduleChanges: [] };
  }

  // Calculate the date offset in days between source and target quotes
  // Using date-fns to avoid timezone issues
  const dateDiffInDays = differenceInDays(
    parseISO(targetContractStartDate),
    parseISO(sourceContractStartDate),
  );

  // Helper function to adjust a date by the calculated difference
  const adjustDate = (dateStr: string): string => {
    const adjustedDate = addDays(parseISO(dateStr), dateDiffInDays);
    return format(adjustedDate, 'yyyy-MM-dd');
  };

  // Helper function to convert an offering to request format
  const convertOfferingToRequest = (
    offering: IQuoteOfferingRespSchema,
  ): IQuoteOfferingReqSchema => {
    return {
      offeringId: offering.offeringId,
      rateId: offering.rateId,
      subscriptionId: offering.subscriptionId,
      description: offering.description,
      // Copy discount IDs from the original offering
      discountIds: offering.discounts?.length
        ? offering.discounts.map((d) => d.id)
        : null,
      // Schedule field is used only for creating new schedule changes, set below
      schedule: null,
      // Adjust start and end dates relative to the new quote's contract period
      startDate: adjustDate(offering.startDate),
      endDate: adjustDate(offering.endDate),
      items: offering.items.map((item) => ({
        customId: item.customId,
        productId: item.productId,
        sku: item.sku,
        description: item.description,
        // Preserve the original quantity from the imported offering
        quantity: item.quantity,
        // Copy custom discount information from the original item
        customDiscountType: item.customDiscountType,
        customDiscountAmountOrPercent: item.customDiscountAmountOrPercent,
        customDiscountName: item.customDiscountName,
        // Convert customPrice from response format to request format
        customPrice: item.customPrice
          ? {
              id: item.customPrice.id,
              unitAmount: item.customPrice.amount,
            }
          : null,
        options: item.options,
        isSelected: item.isSelected,
        position: item.position,
        incrementalQuantity: item.incrementalQuantity,
      })),
    };
  };

  // Separate parent offerings and child offerings (schedule changes)
  const parentOfferings: IQuoteOfferingRespSchema[] = [];
  const childOfferings: Array<{
    oldParentId: string;
    parentOfferingId: string;
    startDate: string;
    endDate: string;
    offering: IQuoteOfferingRespSchema;
  }> = [];

  // Create a map of offering IDs to their offeringId for easy lookup
  const offeringIdMap: Record<string, string> = {};
  existingOfferings.forEach((offering) => {
    offeringIdMap[offering.id] = offering.offeringId;
    if (!offering.parentQuoteOfferingId) {
      parentOfferings.push(offering);
    }
  });

  // Collect child offerings with parent offeringId
  existingOfferings.forEach((offering) => {
    if (offering.parentQuoteOfferingId) {
      const parentOfferingId = offeringIdMap[offering.parentQuoteOfferingId];
      if (parentOfferingId) {
        childOfferings.push({
          oldParentId: offering.parentQuoteOfferingId,
          parentOfferingId,
          startDate: offering.startDate,
          endDate: offering.endDate,
          offering,
        });
      }
    }
  });

  // Convert parent offerings to request format
  const parentOfferingsReq = parentOfferings.map((offering) =>
    convertOfferingToRequest(offering),
  );

  // Convert schedule changes to request format
  // These will be created in a second phase after parent IDs are known
  const scheduleChangesReq = childOfferings.map(
    ({ oldParentId, parentOfferingId, startDate, endDate, offering }) => {
      const offeringReq = convertOfferingToRequest(offering);
      // The schedule field will be set by the import modal once it has the new parent ID
      // Adjust the schedule change dates relative to the new quote's contract period
      return {
        oldParentId,
        parentOfferingId,
        startDate: adjustDate(startDate),
        endDate: adjustDate(endDate),
        offering: offeringReq,
      };
    },
  );

  return {
    parentOfferings: parentOfferingsReq,
    scheduleChanges: scheduleChangesReq,
  };
};

/**
 * Import schedule changes after parent offerings have been created
 *
 * This function:
 * 1. Fetches the updated quote to get newly created parent offering IDs
 * 2. Maps offeringIds to new quote offering IDs
 * 3. Creates schedule changes with correct parent references
 *
 * @param quoteId - The ID of the quote to add schedule changes to
 * @param scheduleChanges - Array of schedule changes to import
 * @param fetchQuoteFn - Function to fetch the quote (typically from useQuote hook)
 * @returns The updated quote after all schedule changes have been created
 */
export const importScheduleChanges = async (
  quoteId: string,
  scheduleChanges: Array<{
    oldParentId: string;
    parentOfferingId: string;
    startDate: string;
    endDate: string;
    offering: IQuoteOfferingReqSchema;
  }>,
  fetchQuoteFn: (quoteId: string) => Promise<IQuoteRespSchema | null>,
): Promise<IQuoteRespSchema | null> => {
  if (scheduleChanges.length === 0) {
    return await fetchQuoteFn(quoteId);
  }

  // Fetch the updated quote to get the newly created parent offering IDs
  const updatedQuote = await fetchQuoteFn(quoteId);
  if (!updatedQuote) {
    throw new Error('Failed to fetch updated quote');
  }

  // Create a map from offeringId to the new quote offering ID
  // This allows us to find the new parent ID for each schedule change
  const offeringIdToNewQuoteOfferingId: Record<string, string> = {};
  updatedQuote.quoteOfferings.forEach((qo) => {
    if (!qo.parentQuoteOfferingId) {
      // This is a parent offering
      offeringIdToNewQuoteOfferingId[qo.offeringId] = qo.id;
    }
  });

  // Create each schedule change
  for (const scheduleChange of scheduleChanges) {
    const newParentId =
      offeringIdToNewQuoteOfferingId[scheduleChange.parentOfferingId];
    if (newParentId) {
      // Set the schedule field to link this to the new parent
      const offeringToCreate: IQuoteOfferingReqSchema = {
        ...scheduleChange.offering,
        schedule: {
          parentQuoteOfferingId: newParentId,
          startDate: scheduleChange.startDate,
          endDate: scheduleChange.endDate,
        },
      };

      // Create the schedule change
      await enqueueForQuote(quoteId, () =>
        doCreateQuoteOffering(quoteId, offeringToCreate),
      );
    }
  }

  // Fetch and return the final quote with all schedule changes
  return await fetchQuoteFn(quoteId);
};

/**
 * Based on quote, defaultContactId, esignConfiguration returns esignIds
 */
export const getDefaultEsignIds = ({
  quote,
  defaultContactId,
  internalEsignContactId,
  isTenantEsignConfigured,
}: {
  quote: IQuoteRespSchema;
  defaultContactId?: Maybe<string>;
  internalEsignContactId?: Maybe<string>;
  isTenantEsignConfigured: boolean;
}) => {
  if (isTenantEsignConfigured && !quote.contacts.esign.length) {
    if (defaultContactId && internalEsignContactId) {
      return [defaultContactId, internalEsignContactId];
    } else if (defaultContactId) {
      return [defaultContactId];
    } else if (internalEsignContactId) {
      return [internalEsignContactId];
    }
  }

  return quote.contacts.esign.map(({ id }) => id);
};

export const getOfferingNameById = (
  id: string,
  offerings: (IOfferingRes | { id: string; name?: string })[],
) => {
  return offerings?.find((offering) => offering.id === id)?.name ?? '';
};

export const findQOByOfferingIdFromQuote = (
  quote: IQuoteRespSchema,
  offeringId: string,
) => {
  return quote.quoteOfferings.find(
    (quoteOffering) => quoteOffering.offeringId === offeringId,
  );
};

export const findQOByQuoteItemIdFromQuote = (
  quote: IQuoteRespSchema,
  quoteItemId: string,
) => {
  const quoteOffering = quote.quoteOfferings.find(({ items }) =>
    items.find(({ id }) => id === quoteItemId),
  );

  return {
    quoteOffering,
    quoteItem: quoteOffering?.items.find(({ id }) => id === quoteItemId),
  };
};

export const findQOBySubscriptionIdFromQuote = (
  quote: IQuoteRespSchema,
  subscriptionId: string,
) => {
  return quote.quoteOfferings.find(
    (quoteOffering) => quoteOffering.subscriptionId === subscriptionId,
  );
};

export const getExplanationItem = (nameInBold: string, description: string) => {
  return (
    <>
      <MText fontWeight="semibold" as="span">
        {nameInBold}
      </MText>
      <MText as="span">{description}</MText>
    </>
  );
};

export const getExplanationFromDeltaView = (
  quote: IQuoteRespSchema,
  deltaView: IQuoteOfferingDeltaView,
) => {
  if (!deltaView.violations?.length) {
    return [];
  }

  const explanations: ReactElement[] = [];

  deltaView.violations.map((viloation) => {
    switch (viloation.label) {
      case QuoteOfferingViolationLabelEnum.DISCONTINUITY_VIOLATION: {
        const quoteOffering = findQOBySubscriptionIdFromQuote(
          quote,
          viloation.subscriptionId!,
        );
        if (quoteOffering) {
          explanations.push(
            getExplanationItem(
              quoteOffering.offeringName,
              "'s quantity or price was adjusted in multiple, discontinuous, periods of the contract term",
            ),
          );
        }
        break;
      }
      case QuoteOfferingViolationLabelEnum.VARIABLE_QUANTITY_VIOLATION: {
        const quoteOffering = findQOBySubscriptionIdFromQuote(
          quote,
          viloation.subscriptionId!,
        );
        if (quoteOffering) {
          explanations.push(
            getExplanationItem(
              quoteOffering.offeringName,
              "'s quantity was amended by multiple amounts during the contract term",
            ),
          );
        }
        break;
      }
      case QuoteOfferingViolationLabelEnum.QUOTE_OFFERING_DESCRIPTION_CHANGE_VIOLATION: {
        const quoteOffering = quote.quoteOfferings.find(
          ({ id }) => id === viloation.quoteOfferingId,
        );
        if (quoteOffering) {
          explanations.push(
            getExplanationItem(
              quoteOffering.offeringName,
              "'s description was adjusted",
            ),
          );
        }
        break;
      }
      case QuoteOfferingViolationLabelEnum.QUOTE_OFFERING_RATE_CHANGE_VIOLATION: {
        const quoteOffering = quote.quoteOfferings.find(
          ({ id }) => id === viloation.quoteOfferingId,
        );
        if (quoteOffering) {
          explanations.push(
            getExplanationItem(
              quoteOffering.offeringName,
              "'s Rate was adjusted",
            ),
          );
        }
        break;
      }
      case QuoteOfferingViolationLabelEnum.QUOTE_OFFERING_DISCOUNT_CHANGE_VIOLATION: {
        const quoteOffering = quote.quoteOfferings.find(
          ({ id }) => id === viloation.quoteOfferingId,
        );
        if (quoteOffering) {
          explanations.push(
            getExplanationItem(
              quoteOffering.offeringName,
              "'s Discount was adjusted",
            ),
          );
        }
        break;
      }
      case QuoteOfferingViolationLabelEnum.QUOTE_ITEM_DISCOUNT_CHANGE_VIOLATION: {
        const { quoteOffering, quoteItem } = findQOByQuoteItemIdFromQuote(
          quote,
          viloation.quoteItemId!,
        );
        if (quoteOffering && quoteItem) {
          explanations.push(
            getExplanationItem(
              `${quoteOffering.offeringName} - ${quoteItem.productName}`,
              "'s Discount was adjusted",
            ),
          );
        }
        break;
      }
      case QuoteOfferingViolationLabelEnum.QUOTE_ITEM_DESCRIPTION_CHANGE_VIOLATION: {
        const { quoteOffering, quoteItem } = findQOByQuoteItemIdFromQuote(
          quote,
          viloation.quoteItemId!,
        );
        if (quoteOffering && quoteItem) {
          explanations.push(
            getExplanationItem(
              `${quoteOffering.offeringName} - ${quoteItem.productName}`,
              "'s description was adjusted",
            ),
          );
        }
        break;
      }
      case QuoteOfferingViolationLabelEnum.QUOTE_ITEM_CUSTOM_DISCOUNT_CHANGE_VIOLATION: {
        const { quoteOffering, quoteItem } = findQOByQuoteItemIdFromQuote(
          quote,
          viloation.quoteItemId!,
        );
        if (quoteOffering && quoteItem) {
          explanations.push(
            getExplanationItem(
              `${quoteOffering.offeringName} - ${quoteItem.productName}`,
              "'s Custom Discount was adjusted",
            ),
          );
        }
        break;
      }
      case QuoteOfferingViolationLabelEnum.QUOTE_ITEM_CUSTOM_PRICE_CHANGE_VIOLATION: {
        const { quoteOffering, quoteItem } = findQOByQuoteItemIdFromQuote(
          quote,
          viloation.quoteItemId!,
        );
        if (quoteOffering && quoteItem) {
          explanations.push(
            getExplanationItem(
              `${quoteOffering.offeringName} - ${quoteItem.productName}`,
              "'s Custom Price was adjusted",
            ),
          );
        }
        break;
      }
      // This is only temporary while we wait for BP-11743: [NOT IN MVP SCOPE] Support mandatory optional updates in delta view
      case QuoteOfferingViolationLabelEnum.QUOTE_ITEM_MANDATORY_OPTIONAL_CHANGE_VIOLATION: {
        const { quoteOffering, quoteItem } = findQOByQuoteItemIdFromQuote(
          quote,
          viloation.quoteItemId!,
        );
        if (quoteOffering && quoteItem) {
          explanations.push(
            getExplanationItem(
              `${quoteOffering.offeringName} - ${quoteItem.productName}`,
              "'s Mandatory Option was adjusted",
            ),
          );
        }
        break;
      }
    }
  });

  return explanations;
};

/**
 * Returns a string that can be used to re-render the quote
 * This is used to track changes to the quote and display config
 * We will rerender if quote is modified or display config is changed, or quote offerings are modified
 * @param quote - The quote to re-render
 * @param displayConfig - The display config to re-render
 */
export const getStrToRerenderQuote = (
  quote?: IQuoteRespSchema | null,
  displayConfig?: IQuoteTemplateConfigSchema | null,
) => {
  if (!quote || !displayConfig) {
    return '';
  }

  return `${quote.processedAt}-${quote.modifyDate}-${quote.quoteOfferings.map((offering) => offering.modifyDate).join('-')}-${quote.purchaseOrderNumber}-${quote.vatNumber}-${Object.entries(
    displayConfig,
  )
    .map(([key, value]) => `${key}-${value}`)
    .join('-')}`;
};

export const getCanChangeLinkedAccount = (quote: IQuoteRespSchema) => {
  return (
    quote?.status === QuoteStatusEnum.DRAFT && quote?.type === QuoteTypeEnum.NEW
  );
};

export const getCalculatedBillingAnchorStartDate = (
  startDate: string,
  endDate: string,
  billingAnchorDay: number,
): Date => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  let year: number;
  let month: number;

  if (start < end) {
    // Normal case: start date is before or equal to end date
    // Start with the start date's month and year
    year = start.getFullYear();
    month = start.getMonth();
  } else {
    // Edge case: start date is after end date
    // Use the end date's month and year
    const endDay = end.getDate();
    year = end.getFullYear();
    month = end.getMonth();

    if (billingAnchorDay > endDay) {
      // shift to previous month
      if (month === 0) {
        year -= 1;
        month = 11;
      } else {
        month -= 1;
      }
    }
  }

  // Create the calculated date with the billing anchor day
  const calculatedDate = new Date(year, month, billingAnchorDay);

  // If the calculated date is before the start date, move to the next month
  if (billingAnchorDay < start.getDate()) {
    calculatedDate.setMonth(calculatedDate.getMonth() + 1);
  }

  return calculatedDate;
};

/**
 * Get a billing anchor relative to a startDate (always in the past)
 * or return the start date if no billing anchor exists is defined
 */
export const getBillingAnchorIfExists = (
  startDate: Date,
  billingAnchor: Maybe<IContractAnchorBillingDay>,
): Date => {
  if (!billingAnchor || !billingAnchor.day || !billingAnchor.month) {
    return startDate;
  }
  if (startDate.getMonth() + 1 >= billingAnchor.month) {
    return new Date(
      startDate.getFullYear(),
      billingAnchor.month - 1,
      billingAnchor.day,
    );
  }
  return new Date(
    startDate.getFullYear() - 1,
    billingAnchor.month - 1,
    billingAnchor.day,
  );
};

export const shouldAllowEsignEdit = (
  quoteStatus?: QuoteStatusEnum,
  allowEsignEditOnQuoteReview?: boolean,
) =>
  allowEsignEditOnQuoteReview &&
  quoteStatus &&
  QUOTE_ESIGN_CONTACT_EDITABLE_STATUSES.has(quoteStatus);

export const hasMixedSubscriptionTimings = (
  rates: IRateResBaseSchema[],
): boolean => {
  const timings = new Set(rates.map((rate) => rate.subscriptionTiming));
  return (
    timings.has(SubscriptionTimingEnum.ADVANCE) &&
    timings.has(SubscriptionTimingEnum.ARREARS)
  );
};
